import React from 'react';
import { Group, Rect, Text, Circle } from 'react-konva';
import { StageItem as StageItemType, Position, Size } from '../../types';

interface StageItemProps {
  id: string;
  item: StageItemType;
  position: Position;
  size: Size;
  rotation: number;
  isSelected: boolean;
  onDragStart: (e: any) => void;
  onDragEnd: (e: any) => void;
  onClick: (e: any) => void;
  onRotate: (id: string, rotation: number) => void;
}

const StageItem: React.FC<StageItemProps> = ({
  id,
  position,
  size,
  rotation,
  isSelected,
  onDragStart,
  onDragEnd,
  onClick,
  onRotate,
  item
}) => {
  // Handle rotation with the rotation handle
  const handleRotateStart = (e: any) => {
    e.cancelBubble = true; // Prevent parent drag
  };
  
  const handleRotateMove = (e: any) => {
    e.cancelBubble = true; // Prevent parent drag
    
    // Calculate the center of the item using the stage's coordinates
    const stage = e.target.getStage();
    const pointerPos = stage.getPointerPosition();
    
    // Use getParent().getAbsolutePosition() to get the actual position
    // accounting for scale, rotation, and any transformations
    const groupNode = e.target.getParent();
    const groupAbsPos = groupNode.getAbsolutePosition();
    
    // Calculate angle between center of item and current mouse position
    const deltaX = pointerPos.x - groupAbsPos.x;
    const deltaY = pointerPos.y - groupAbsPos.y;
    
    // Calculate angle in degrees and add 90° to make rotation start from the top
    let angle = Math.atan2(deltaY, deltaX) * 180 / Math.PI + 90;
    
    // Normalize angle to 0-360 range
    if (angle < 0) angle += 360;
    
    // Round to nearest 15 degrees for better usability
    const snappedAngle = Math.round(angle / 15) * 15;
    
    onRotate(id, snappedAngle);
  };

  return (
    <Group
      id={id}
      x={position.x}
      y={position.y}
      rotation={rotation}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
    >
      <Rect
        id={id}
        width={size.width}
        height={size.height}
        fill="#c8a2c8"
        stroke={isSelected ? "#3498db" : "#9e7e9e"}
        strokeWidth={isSelected ? 2 : 1}
        cornerRadius={5}
        x={-size.width / 2}
        y={-size.height / 2}
      />
      
      <Text
        text="STAGE"
        fontSize={16}
        fontStyle="bold"
        fill="#fff"
        align="center"
        verticalAlign="middle"
        width={size.width}
        height={size.height}
        offsetX={size.width / 2}
        offsetY={size.height / 2}
      />
      
      {/* Stage steps */}
      <Rect
        id={id}
        width={size.width / 3}
        height={size.height / 6}
        fill="#ad8bad"
        stroke={isSelected ? "#3498db" : "#9e7e9e"}
        strokeWidth={isSelected ? 2 : 1}
        cornerRadius={2}
        x={-size.width / 6}
        y={size.height / 2}
      />

      {/* Rotation Handle - only show when selected */}
      {isSelected && (
        <>
          {/* Rotation indicator line */}
          <Rect
            x={0}
            y={-size.height / 2 - 30}
            width={2}
            height={30}
            fill="#3498db"
            offsetX={1}
          />
          
          {/* Rotation handle */}
          <Circle
            x={0}
            y={-size.height / 2 - 40}
            radius={10}
            fill="#3498db"
            stroke="#fff"
            strokeWidth={2}
            draggable
            onDragStart={handleRotateStart}
            onDragMove={handleRotateMove}
            onMouseEnter={(e) => {
              const container = e.target.getStage()?.container();
              if (container) container.style.cursor = 'grab';
            }}
            onMouseLeave={(e) => {
              const container = e.target.getStage()?.container();
              if (container) container.style.cursor = 'default';
            }}
          />
        </>
      )}
    </Group>
  );
};

export default StageItem; 